import React from "react";
import { styled } from "@mui/material/styles";
import {
  ImageField,
  Edit as BaseEdit,
  SimpleForm,
  BooleanInput,
  SelectInput,
  TextInput,
  ImageInput,
  NumberInput,
  required,
  Toolbar,
  SaveButton,
  DeleteButton,
  useNotify,
  useRedirect,
  useRefresh,
  FormDataConsumer,
  useGetOne,
  TextField,
} from "react-admin";
import { BooleanInputs } from "./Create";
import { useProvider } from "../ProviderContext";
import { SelectInputCategory } from "../Category/SelectInput";
const PREFIX = "Edit";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

const StyledBaseEdit = styled(BaseEdit)({
  [`& .${classes.inlineBlock}`]: {
    display: "inline-flex",
    marginRight: "1rem",
  },
});

function formatImage(value) {
  if (!value) {
    return null;
  }

  // Si es un string (URL de la imagen existente)
  if (typeof value === "string") {
    return { src: value };
  }

  // Si es un File (nueva imagen seleccionada)
  if (value instanceof File) {
    return { src: URL.createObjectURL(value) };
  }

  // Si ya es un objeto con src
  if (value && typeof value === "object" && value.src) {
    return value;
  }

  // Si es un FileList, tomar el primer archivo
  if (value && value.length && value[0] instanceof File) {
    return { src: URL.createObjectURL(value[0]) };
  }

  return value;
}

const CustomToolbar = (props) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();

  const handleDeleteSuccess = () => {
    notify("Eliminado con éxito", { type: "success" });
    redirect("/services");
    refresh();
  };

  const handleDeleteFailure = (error) => {
    notify(`Error: ${error.message}`, { type: "warning" });
  };

  return (
    <Toolbar {...props} style={{ display: "flex", justifyContent: "space-between" }}>
      <SaveButton />
      <DeleteButton
        mutationMode="pessimistic"
        onSuccess={handleDeleteSuccess}
        onFailure={handleDeleteFailure}
        confirmTitle="Confirmación de eliminación"
        confirmContent="¿Estás seguro de que deseas eliminar este elemento? Esta acción no se puede deshacer."
      />
    </Toolbar>
  );
};

export const PinnedInputs = () => {
  return (
    <FormDataConsumer>
      {({ formData }) => (
        <>
          <BooleanInput source="pinned.isPinned" label="Fijar servicio" defaultValue={false} />
          {formData.pinned && formData.pinned.isPinned && (
            <TextInput source="pinned.tag" label="Etiqueta del Servicio Destacado" />
          )}
          <BooleanInput source="package.isEnabled" label="Habilitar paquetes" defaultValue={false} />
          {formData.package && formData.package.isEnabled && (
            <TextInput source="package.quantity" label="Cantidad" validate={[required("Cantidad es requerido")]} />
          )}
        </>
      )}
    </FormDataConsumer>
  );
};

export const Edit = ({ ...props }) => {
  const profile = JSON.parse(localStorage.getItem("profile"));
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
  const { data: provider } = useGetOne("providers", { id: providerId });
  const { provider: providerContext } = useProvider();
  const intervalMinutes = providerContext?.intervalMinutes || 30;

  // Generar choices dinámicamente según intervalMinutes
  const maxMinutes = 8 * 60; // 8 horas máximo
  const choices = Array.from({ length: Math.floor(maxMinutes / intervalMinutes) + 1 }, (_, i) => {
    const value = i * intervalMinutes;
    let name;
    if (value === 0) {
      name = "0 Minutos";
    } else if (value < 60) {
      name = `${value} Minutos`;
    } else if (value % 60 === 0) {
      name = `${value / 60} Hora${value === 60 ? "" : "s"}`;
    } else {
      name = `${Math.floor(value / 60)} Hora${Math.floor(value / 60) > 1 ? "s" : ""} ${value % 60} Minutos`;
    }
    return { id: value, name };
  });

  return (
    <StyledBaseEdit {...props}>
      <SimpleForm toolbar={<CustomToolbar />}>
        <TextInput validate={[required("El nombre es requerido")]} source="name" label="Nombre del Servicio" />
        <ImageInput
          validate={[required("La imagen es requerida")]}
          format={formatImage}
          source="imageURL"
          label="Imagen de Servicio"
          accept=".jpeg,.jpg,.png,.jfif"
          placeholder={<p>Arrastra tu imagen o haz clic aquí.</p>}
        >
          <ImageField source="src" title="Imagen" />
        </ImageInput>
        <TextInput
          validate={[required("La descripción es requerida")]}
          multiline
          source="description"
          label="Descripción"
        />
        <NumberInput
          validate={[required("El precio es requerido")]}
          source="unitPrice"
          placeholder="Bs."
          label="Precio Unitario"
        />

        <BooleanInputs />

        <NumberInput
          source="availableAfterHours"
          placeholder="horas"
          label="Tiempo de reserva anticipada (horas)"
          validate={[required("Tiempo de reserva es requerido")]}
          defaultValue={24}
        />
        <SelectInputCategory />
        <SelectInput
          validate={[required()]}
          source="unitEstimatedWorkMinutes"
          label="Tiempo de servicio estimado"
          choices={choices}
        />
        <NumberInput source="commissionEmployee" label="Comisión de Empleado (%)" placeholder="%" defaultValue={0} />
        <SelectInput
          validate={[required("El método es requerido")]}
          source="method"
          label="Método"
          choices={[
            { id: "En sucursal", name: "En sucursal" },
            { id: "A domicilio", name: "A domicilio" },
            { id: "Ambos", name: "Ambos" },
          ]}
        />
        <BooleanInput validate={[required("Habilitado es requerido")]} source="enabled" label="Habilitado" />
        <PinnedInputs />
        {provider?.reminderConfirmed && (
          <NumberInput source="reminderIntervalDays" label="Tiempo del recordatorio en días" defaultValue={0} />
        )}
      </SimpleForm>
    </StyledBaseEdit>
  );
};
